import * as React from 'react'
import { createPortal } from 'react-dom'
import { matchPath, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { preloadPage } from '@/app/lazy-pages'
import {
  BadgeCheck,
  BarChart3,
  BookOpen,
  ChevronRight,
  ChevronsUpDown,
  CreditCard,
  Key,
  LayoutDashboard,
  Lock,
  LogOut,
  Moon,
  PackageSearch,
  Search,
  Shield,
  ShoppingCart,
  Sun,
  Wrench,
} from 'lucide-react'

import { supabase, isSupabaseConfigured } from '@/lib/supabase-client'
import { issueGrafanaSso } from '@/services/admin-api'
import { clearSsoCookies } from '@/lib/sso-logout'
import { issueBlogSso } from '@/services/admin-api'
import { useUserProfile } from '@/app/providers/user-profile-provider'
import { useSubscriptionContext } from '@/app/providers/subscription-provider'
import { useFreeAccess } from '@/hooks/use-free-access'
import { useDataMode } from '@/app/providers/data-mode-provider'
import { toast } from 'sonner'
import { appRoutes } from '@/app/routes'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  SidebarRail,
} from '@/components/ui/sidebar'
import { useSidebar } from '@/components/ui/sidebar-context'
import { PaymentIssueCard } from '@/components/billing/payment-issue-card'
import { NotificationBell } from '@/components/notifications/notification-bell'
import { useTheme } from '@/hooks/use-theme'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { useSupportModal } from '@/app/providers/support-modal-provider'
import { useNewBadgesEnabled } from '@/hooks/use-new-badges-enabled'
import { useDashboardAccess } from '@/hooks/use-dashboard-access'
import { VIEW_AS_RE } from '@/lib/amazon-view-as'
import { useAccessMap } from '@/hooks/use-access-map'
import { decidePageAccess } from '@/lib/page-access'
import { isKaiNavUnlocked } from '@/components/layout/kai-nav-lock'
import { isNavItemNew } from '@/lib/nav-badge'
import { NavNewDot, NavNewPill } from '@/components/layout/nav-new-badge'
import {
  teamOptionsMock,
  type TeamOption,
} from '@/mocks'
import { BRAND } from '@/config/brand.generated'
import { groupLabelLang } from './group-label-lang'

type NavItem = {
  title: string
  url: string
  icon?: React.ComponentType<{ className?: string }>
  description?: string
  externalUrl?: string
  modalAction?: boolean
  /** Route link değil; tıklamada tetiklenen aksiyon (ör. Grafana SSO). */
  action?: () => void
  /** #770: "Yeni" rozeti — bkz. AppRoute.newUntil. */
  newUntil?: string
  /** #1029: Free'ye açık olmayan Pro sayfası (yalnız sunucu "limited" modundayken). Bağlantı kapanmaz. */
  locked?: boolean
}

type NavSectionConfig = {
  label: string
  paths: string[]
  collapsible?: boolean
  position?: 'top' | 'bottom'
  hideLabel?: boolean
  icon?: React.ComponentType<{ className?: string }>
}

const routeMap = new Map(
  appRoutes.map((route) => [route.path, route] as const),
)

// Phase 76 UI-14: Her zaman disable olan linkler (herkes için).
// Product Database gibi "koşullu disable" (admin hariç) senaryoları Sidebar()
// içinde useMemo ile hesaplanan `disabledNavConfig`'e eklenir.
const staticDisabledNavConfig: Record<string, { heading: string; description: string }> = {
  // '/app/chatbot' burada DEĞİL: Kai kilidi erişim haritasına bağlı (koşullu, aşağıda `disabledNavConfig`).
  '/app/review-automation': {
    heading: 'Çok yakında',
    description: '',
  },
}

/**
 * #402: Admin'den başkasının göremediği tüm sayfalar tek grupta toplanır.
 * Grup ADMIN İÇİN oluşturulur; non-admin sidebar'ı hiç etkilenmez (o kullanıcı
 * bu item'ları zaten görmüyordu — tek istisna Product Database, o non-admin'de
 * "Çok yakında" disabled olarak kendi bölümünde KALIR).
 */
const ADMIN_SECTION_LABEL = 'Admin'

/** Hover ile açılan grubun, imleç ayrılınca kapanma gecikmesi (ms).
 *  0 olursa label'dan içeriğe inerken oluşan 1-2 px'lik boşlukta menü
 *  titreyerek kapanır; 250 ms insan hareketini yutacak kadar, "takılı kaldı"
 *  hissi verecek kadar uzun değil. */
const ADMIN_HOVER_CLOSE_DELAY_MS = 250

const navSections: NavSectionConfig[] = [
  {
    label: 'Dashboard',
    paths: ['/app/dashboard'],
    hideLabel: true,
  },
  {
    label: 'Product Research',
    collapsible: true,
    icon: PackageSearch,
    paths: [
      '/app/deepsearch',
      '/app/new-release',
      '/app/product-database',
      '/app/sales-estimator',
      '/app/product-list',
    ],
  },
  {
    label: 'Keyword Optimization',
    collapsible: true,
    icon: Key,
    paths: ['/app/freqfinder', '/app/key-insight', '/app/keyword-lists'],
  },
  {
    label: 'Analytics & Performance',
    collapsible: true,
    icon: BarChart3,
    paths: ['/app/listing-builder', '/app/profit-calculator', '/app/review-automation'],
  },
  {
    label: 'Amazon Seller',
    collapsible: true,
    icon: ShoppingCart,
    paths: ['/app/rank-tracker', '/app/amazon/dashboard', '/app/sqp', '/app/amazon/advertising'],
  },
  {
    label: 'Kai',
    paths: ['/app/chatbot'],
    hideLabel: true,
  },
  {
    label: 'Deneyim & İçgörü',
    position: 'bottom',
    icon: BookOpen,
    paths: ['/app/academy', '/app/extension'],
  },
  {
    label: 'Destek & Yönetim',
    position: 'bottom',
    icon: Wrench,
    paths: ['/app/billing', '/app/settings', '/app/admin/allowed-emails', '/app/admin/notifications', '/app/admin/anchor-review', '/app/admin/content', '/app/admin/extension-features', '/app/support'],
  },
]

export function Sidebar() {
  const location = useLocation()
  const { profile } = useUserProfile()
  // Yönetici sıkı görünümü: ölçüler ui/sidebar.tsx'te compact bağlamıyla gelir; burada
  // yalnız bu dosyada elle verilen boşluklar koşula bağlanır (yönetici değilse hiçbiri eklenmez).
  const { compact, visualState } = useSidebar()
  // #770: "Yeni" rozeti kullanıcı tercihine bağlı (Ayarlar > "Yeni etiketlerini göster").
  const { enabled: showNewBadges } = useNewBadgesEnabled()

  // Phase 76 UI-14: disabledNavConfig artık profile.isAdmin'e reaktif.
  // Admin olmayan kullanıcı Product Database linkini "Çok yakında" disabled görür;
  // admin kullanıcı linki normal kullanır.
  // #825 dilim 3: merkezi erişim tablosu kararları (yalnız kısıtlar; harita yokken boş etki).
  const { data: accessMap } = useAccessMap()
  // #1029: kilit simgesi YALNIZ sunucu "limited" modundayken; kapalıyken menü bit-bit eskisi gibi.
  const { isPro: sidebarIsPro } = useSubscriptionContext()
  const { limited: freeLimited, isOpenPath: isFreeOpen } = useFreeAccess()

  const disabledNavConfig = React.useMemo<Record<string, { heading: string; description: string }>>(
    () => {
      const base: Record<string, { heading: string; description: string }> = {
        ...staticDisabledNavConfig,
      }
      // Kai (D-04, D-06): yalnız `page_chatbot.allowed === true` açar; yükleniyor/hata/anahtar yok/false → kilitli.
      if (!isKaiNavUnlocked(accessMap)) {
        base['/app/chatbot'] = { heading: 'Çok yakında', description: '' }
      }
      if (!profile.isAdmin) {
        // Yasin (13 Eki 2026): erişemediği sayfa için açıklama gerekmez; yalnız "Çok yakında".
        base['/app/product-database'] = { heading: 'Çok yakında', description: '' }
      }
      // Panelden "Çok yakında" olarak işaretlenen sayfalar: bağlantı kapalı görünür (yeni içerik, mevcut desen).
      for (const route of appRoutes) {
        if (decidePageAccess(accessMap, route) === 'coming_soon' && !base[route.path]) {
          base[route.path] = { heading: 'Çok yakında', description: '' }
        }
      }
      return base
    },
    [profile.isAdmin, accessMap],
  )

  // Phase 06 SSO-01: Grafana tek-tık erişimi. Sidebar link'i değil AKSİYON —
  // backend issue endpoint'ine Bearer-JWT POST at, imzalı cookie set edilince
  // düz navigasyonla /grafana/'a git (nginx auth_request cookie'yi doğrular).
  const handleGrafanaSso = React.useCallback(async () => {
    try {
      const res = await issueGrafanaSso()
      if (res?.ok) {
        window.location.href = res.redirect ?? '/grafana/'
      } else {
        toast.error('Grafana erişimi açılamadı, lütfen tekrar deneyin.')
      }
    } catch {
      toast.error('Grafana erişimi açılamadı, lütfen tekrar deneyin.')
    }
  }, [])

  // #701 (BLG-03): Blog da link değil AKSİYON. Her tıklamada ÖNCE bilet alınır
  // (süresi dolmuş çerez sorun olmaz), sonra düz gezinmeyle `/blog/`.
  // Hedef SABİT: yanıttaki `redirect` bilerek kullanılmaz (sunucu yanıtı gezinme
  // hedefini belirleyemez; Grafana'dan bilinçli sapma). `?blog_sso=expired`
  // parametresi hiçbir yerde yorumlanmaz: otomatik bilet/gezinme yok, böylece
  // yönlendirme döngüsü oluşamaz; admin düğmeye yeniden basar.
  const blogSsoInFlight = React.useRef(false)
  const handleBlogSso = React.useCallback(async () => {
    // İstek sürerken ikinci tıklama yeni bilet istemez.
    if (blogSsoInFlight.current) return
    blogSsoInFlight.current = true
    try {
      const res = await issueBlogSso()
      if (res?.ok) {
        window.location.href = '/blog/'
      } else {
        toast.error('Blog açılamadı, lütfen tekrar deneyin.')
      }
    } catch {
      toast.error('Blog açılamadı, lütfen tekrar deneyin.')
    } finally {
      blogSsoInFlight.current = false
    }
  }, [])

  const builtSections = navSections.map((section) => ({
    label: section.label,
    collapsible: section.collapsible ?? false,
    position: section.position ?? 'top',
    hideLabel: section.hideLabel ?? false,
    icon: section.icon,
    items: section.paths
      .map((path) => routeMap.get(path))
      .filter((route): route is (typeof appRoutes)[number] => Boolean(route))
      // Phase 76 UI-14: adminOnly route'lar non-admin için gizlenir —
      // İSTİSNA: /app/product-database, non-admin'e disabled gösterilir (Kai pattern)
      .filter((route) => {
        if (!route.adminOnly) return true
        if (profile.isAdmin) return true
        // Non-admin + adminOnly: Product Database özel case (göster ama disabled)
        if (route.path === '/app/product-database') return true
        return false
      })
      // #825 dilim 3: tablo "erişim yok + gizli" diyorsa menüden çıkar (yalnız kısıtlama).
      .filter((route) => decidePageAccess(accessMap, route) !== 'denied')
      .map((route): NavItem => ({
        title: route.label,
        url: route.path,
        icon: route.icon,
        description: route.description,
        externalUrl: route.externalUrl,
        modalAction: route.modalAction,
        newUntil: route.newUntil,
        locked: Boolean(
          route.proOnly && !route.adminOnly && !sidebarIsPro && !profile.isAdmin && freeLimited && !isFreeOpen(route.path),
        ),
      })),
  }))

  // #402: admin-only item'ları kendi bölümlerinden ÇIKAR, tek "Admin" grubunda
  // topla. Yalnız admin dalında çalışır — non-admin için builtSections'a hiç
  // dokunulmaz, o kullanıcının sidebar'ı bit-bit aynı kalır (kabul ölçütü).
  //
  // Sıra korunur: item'lar bölüm sırasıyla toplanır, böylece grup içi düzen
  // rastgele değil öngörülebilir olur.
  const adminItems: NavItem[] = []
  if (profile.isAdmin) {
    for (const section of builtSections) {
      const keep: NavItem[] = []
      for (const item of section.items) {
        if (routeMap.get(item.url)?.adminOnly) {
          adminItems.push(item)
        } else {
          keep.push(item)
        }
      }
      section.items = keep
    }
    // Phase 06 SSO-02: Grafana route DEĞİL aksiyon; admin grubunun sonuna eklenir.
    // Güvenlik backend'de (@require_role → 403); bu gate yalnızca UX.
    adminItems.push({
      title: 'Grafana',
      url: '__grafana_sso__',
      action: handleGrafanaSso,
    })
    // #701: Blog da route DEĞİL aksiyon; Grafana'nın hemen altında, yalnız admin.
    // Gerçek koruma backend'de (@require_role) ve /blog kapısında; bu gate yalnızca UX.
    adminItems.push({
      title: 'Blog',
      url: '__blog_sso__',
      action: handleBlogSso,
    })
  }

  const allSections = builtSections.filter((section) => section.items.length > 0)

  if (adminItems.length > 0) {
    allSections.push({
      label: ADMIN_SECTION_LABEL,
      // Alt bloğa DEĞİL kaydırılabilir üst bölgeye konur. Alt blok (`mt-auto`)
      // sabit yükseklikli değil ve daralamaz: 10 item'lık grup orada açılınca
      // 720px'lik ekranda tüm üst menüyü VE kullanıcı/tema alanını ekrandan
      // taşırıyordu (staging'de görüldü). SidebarContent `min-h-0 overflow-auto`
      // olduğu için grup burada açıldığında yalnız o bölge kayar.
      position: 'top',
      collapsible: true,
      hideLabel: false,
      icon: Shield,
      items: adminItems,
    })
  }

  const topSections = allSections.filter((s) => s.position === 'top')
  const bottomSections = allSections.filter((s) => s.position === 'bottom')

  // Accordion: only one collapsible section open at a time
  const initialOpen = React.useMemo(() => {
    const saved = getSavedOpenSection()
    if (saved) return saved
    // Default: open the section containing the active route
    const activeSection = allSections.find(
      (s) => s.collapsible && s.items.some(
        (item) => !disabledNavConfig[item.url] && matchPath({ path: item.url, end: item.url === '/' }, location.pathname)
      )
    )
    return activeSection?.label ?? null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [openSection, setOpenSection] = React.useState<string | null>(initialOpen)

  const handleToggleSection = React.useCallback((label: string) => {
    setOpenSection((prev) => {
      const next = prev === label ? null : label
      saveOpenSection(next)
      return next
    })
  }, [])

  // #402: Admin grubu accordion'a DAHİL DEĞİL, kendi bağımsız durumu var.
  // Sebep: accordion tek-açık; hover ile Admin açılsa kullanıcının açtığı
  // bölüm imleç oradan geçerken kapanırdı. Ayrıca localStorage'a YAZILMAZ.
  // D-13-15: admin kullanıcı ilk renderda grubu açık görür; non-admin için
  // adminItems boş kaldığından grup zaten hiç render edilmez.
  const [adminOpen, setAdminOpen] = React.useState<boolean>(() => profile.isAdmin)
  const adminCloseTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearAdminCloseTimer = React.useCallback(() => {
    if (adminCloseTimer.current) {
      clearTimeout(adminCloseTimer.current)
      adminCloseTimer.current = null
    }
  }, [])

  const openAdminGroup = React.useCallback(() => {
    clearAdminCloseTimer()
    setAdminOpen(true)
  }, [clearAdminCloseTimer])

  // Gecikmeli kapanış: label ile içerik arasındaki boşlukta ya da bağlantıya
  // giderken imleç bir kare "dışarıda" kalsa bile menü titremez.
  const closeAdminGroupDeferred = React.useCallback(() => {
    clearAdminCloseTimer()
    adminCloseTimer.current = setTimeout(() => {
      adminCloseTimer.current = null
      setAdminOpen(false)
    }, ADMIN_HOVER_CLOSE_DELAY_MS)
  }, [clearAdminCloseTimer])

  React.useEffect(() => clearAdminCloseTimer, [clearAdminCloseTimer])

  const handleToggleAdminGroup = React.useCallback(() => {
    clearAdminCloseTimer()
    setAdminOpen((prev) => !prev)
  }, [clearAdminCloseTimer])

  const navUser = React.useMemo(
    () => {
      const firstName = profile.firstName.trim()
      const lastName = profile.lastName.trim()
      const displayName = [firstName, lastName].filter(Boolean).join(' ') || `${BRAND.appName} Kullanıcısı`

      return {
        name: displayName,
        email: profile.email,
      }
    },
    [profile.email, profile.firstName, profile.lastName],
  )

  return (
    <SidebarRoot
      collapsible="icon"
      className="border-r border-sidebar-border"
    >
      <SidebarHeader className={cn('px-3 py-4', compact && 'py-[10px]')}>
        <TeamSwitcher teams={teamOptionsMock} />
      </SidebarHeader>
      <SidebarContent className="px-2" role="navigation" aria-label="Ana menü">
        {topSections.map((section) => (
          <NavSection
            key={section.label}
            label={section.label}
            items={section.items}
            pathname={location.pathname}
            collapsible={section.collapsible}
            hideLabel={section.hideLabel}
            isOpen={
              section.label === ADMIN_SECTION_LABEL
                ? adminOpen
                : openSection === section.label
            }
            onToggle={
              section.label === ADMIN_SECTION_LABEL
                ? handleToggleAdminGroup
                : handleToggleSection
            }
            hoverBehavior={
              section.label === ADMIN_SECTION_LABEL
                ? { onOpen: openAdminGroup, onCloseDeferred: closeAdminGroupDeferred }
                : undefined
            }
            markActive={section.label === ADMIN_SECTION_LABEL}
            sectionIcon={section.icon}
            disabledNavConfig={disabledNavConfig}
            showNewBadges={showNewBadges}
          />
        ))}
      </SidebarContent>
      <div className={cn('mt-auto px-2 pb-1', compact && 'shrink-0')} role="navigation" aria-label="Alt menü">
        {bottomSections.map((section) => (
          <NavSection
            key={section.label}
            label={section.label}
            items={section.items}
            pathname={location.pathname}
            collapsible={section.collapsible}
            hideLabel={section.hideLabel}
            isOpen={
              section.label === ADMIN_SECTION_LABEL
                ? adminOpen
                : openSection === section.label
            }
            onToggle={
              section.label === ADMIN_SECTION_LABEL
                ? handleToggleAdminGroup
                : handleToggleSection
            }
            hoverBehavior={
              section.label === ADMIN_SECTION_LABEL
                ? { onOpen: openAdminGroup, onCloseDeferred: closeAdminGroupDeferred }
                : undefined
            }
            markActive={section.label === ADMIN_SECTION_LABEL}
            sectionIcon={section.icon}
            disabledNavConfig={disabledNavConfig}
            showNewBadges={showNewBadges}
          />
        ))}
        <NotificationBell />
        <SidebarThemeToggle />
      </div>
      <SidebarFooter className={cn('px-2 pb-4', compact && 'py-2')}>
        {visualState !== 'collapsed' && <PaymentIssueCard />}
        <LayoutGroup id="sidebar-footer">
          <NavUser user={navUser} />
        </LayoutGroup>
      </SidebarFooter>
      <SidebarRail />
    </SidebarRoot>
  )
}

function TeamSwitcher({ teams }: { teams: TeamOption[] }) {
  const { isMobile, visualState } = useSidebar()
  const { mode, setMode } = useDataMode()
  const { isPro, entitlementUnknown } = useSubscriptionContext()
  const { theme } = useTheme()
  const logoSrc = theme === 'light' ? '/sensei-logo.png' : '/sensei-logo-white.png'

  const activeTeam = React.useMemo(
    () => teams.find((team) => team.mode === mode) ?? teams[0],
    [mode, teams],
  )

  if (!activeTeam) {
    return null
  }

  const handleSelect = (team: TeamOption) => {
    if (team.mode === mode) {
      return
    }

    setMode(team.mode)

    if (team.mode === 'real') {
      toast('Live mod aktifleştirildi', {
        description:
          'Gerçek veriler Amazon hesabı bağlantısı tamamlandığında otomatik olarak yüklenir. Şu an demo içerikleri gizlendi.',
      })
    } else {
      toast('Demo mod aktifleştirildi', {
        description: `${BRAND.appName} arayüzü örnek verilerle görüntüleniyor; backend istekleri durduruldu.`,
      })
    }
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:grid group-data-[collapsible=icon]:place-items-center group-data-[collapsible=icon]:gap-0"
            >
              <div className="grid size-9 shrink-0 place-items-center rounded-lg group-data-[collapsible=icon]/menu-button:mx-auto">
                <img src={logoSrc} alt={activeTeam.name} className="h-7 w-7" />
              </div>
              {visualState !== 'collapsed' ? (
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold tracking-tight">
                    {activeTeam.name}
                    <span className="ml-1 inline-flex items-center rounded bg-white px-[3px] py-px align-sub text-[7px] font-bold uppercase leading-none text-brand-blue">
                      Beta
                    </span>
                  </span>
                  <span className="flex items-center gap-1.5 truncate text-xs text-sidebar-foreground/60">
                    {activeTeam.plan}
                    <span className={cn(
                      'inline-flex shrink-0 items-center rounded px-1 py-0.5 text-[9px] font-bold uppercase leading-none',
                      isPro
                        ? 'bg-brand-chartreuse/15 text-brand-chartreuse'
                        : 'bg-sidebar-foreground/10 text-sidebar-foreground/40'
                    )}>
                      {entitlementUnknown ? '—' : isPro ? 'Pro' : 'Free'}
                    </span>
                  </span>
                </div>
              ) : null}
              {visualState !== 'collapsed' ? (
                <ChevronsUpDown className="ml-auto size-4" />
              ) : null}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg border-sidebar-border bg-sidebar text-sidebar-foreground shadow-xl"
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            {teams.map((team) => (
              <DropdownMenuItem
                key={team.name}
                onClick={() => handleSelect(team)}
                className="p-2 focus:bg-sidebar-accent focus:text-sidebar-accent-foreground"
                data-state={team.mode === mode ? 'active' : undefined}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span>{team.plan}</span>
                  {team.mode === mode ? (
                    <span className="text-xs text-sidebar-foreground/50">Aktif</span>
                  ) : null}
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

const SIDEBAR_OPEN_SECTION_KEY = 'sensei:sidebar-open-section'

function getSavedOpenSection(): string | null {
  try {
    return localStorage.getItem(SIDEBAR_OPEN_SECTION_KEY)
  } catch {
    return null
  }
}

function saveOpenSection(label: string | null) {
  try {
    if (label) {
      localStorage.setItem(SIDEBAR_OPEN_SECTION_KEY, label)
    } else {
      localStorage.removeItem(SIDEBAR_OPEN_SECTION_KEY)
    }
  } catch { /* ignore */ }
}

type DisabledNavConfig = Record<string, { heading: string; description: string }>

function NavSection({
  label,
  items,
  pathname,
  collapsible = false,
  hideLabel = false,
  isOpen = false,
  onToggle,
  hoverBehavior,
  markActive = false,
  sectionIcon,
  disabledNavConfig,
  showNewBadges = false,
}: {
  label: string
  items: NavItem[]
  pathname: string
  collapsible?: boolean
  hideLabel?: boolean
  isOpen?: boolean
  onToggle?: (label: string) => void
  /** #402: verilirse grup imleç üzerine gelince açılır (yalnız Admin grubu). */
  hoverBehavior?: { onOpen: () => void; onCloseDeferred: () => void }
  /**
   * #402: grup başlığı, içindeki sayfa aktifken vurgulanır. Yalnız Admin
   * grubunda açık — diğer bölümlere uygulansaydı normal kullanıcının
   * sidebar'ının GÖRÜNÜMÜ değişirdi (kapsam dışı).
   */
  markActive?: boolean
  sectionIcon?: React.ComponentType<{ className?: string }>
  disabledNavConfig: DisabledNavConfig
  /** #770: kullanıcı "Yeni etiketlerini göster" ayarını kapattıysa false. */
  showNewBadges?: boolean
}) {
  const { visualState, isMobile, compact } = useSidebar()
  const isVisuallyCollapsed = visualState === 'collapsed'
  const SectionIcon = sectionIcon
  const groupRef = React.useRef<HTMLDivElement | null>(null)

  // #770: bölüm içinde en az bir "yeni" item varsa, bölüm daraltılmışken
  // (accordion kapalı ya da ikon-only rail) kullanıcı fark etsin diye
  // başlık/ikon üzerinde küçük nokta gösterilir.
  const hasNewItem = showNewBadges && items.some((item) => isNavItemNew(item.newUntil))

  // #402: hover ile açılan grup, kaydırılabilir menü bölgesinin GÖRÜNMEYEN
  // kısmında kalabilir — kısa ekranlarda (720p) bölge ~240px, 10 maddelik grup
  // ~470px. Kullanıcı imleci getirir, "hiçbir şey olmadı" sanır (staging'de
  // birebir görüldü). Açılışta grubu bölgenin ÜSTÜNE kaydırıyoruz.
  //
  // `block:'start'` ZORUNLU: `'nearest'` ile tarayıcı "üst kenar zaten
  // görünüyor" deyip HİÇ kaydırmıyor — grup açık kalıyor ama içerik hâlâ
  // görünmez. Staging ölçümü: scrollTop 169, gereken ~600.
  const scrollGroupIntoView = React.useCallback(() => {
    const node = groupRef.current
    // jsdom ve eski tarayıcılarda tanımsız olabilir — kaydırma bir konfor
    // iyileştirmesi, yokluğunda menü yine çalışır.
    if (typeof node?.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }, [])

  const shouldScrollIntoView = Boolean(hoverBehavior) && isOpen
  // compact (yönetici): kaydırılan kap artık sidebar'ın tamamı. Sayfa ilk açıldığında
  // grup zaten açık geldiği için kaydırırsak logo/Dashboard satırı ekran dışına itilir.
  // Bu yüzden compact'ta yalnız kullanıcı grubu SONRADAN açınca kaydırılır.
  const prevOpenRef = React.useRef(isOpen)
  const userOpenedRef = React.useRef(!compact)
  React.useEffect(() => {
    const wasOpen = prevOpenRef.current
    prevOpenRef.current = isOpen
    if (!shouldScrollIntoView) return
    if (compact && wasOpen === isOpen && !userOpenedRef.current) return
    userOpenedRef.current = true
    const frame = requestAnimationFrame(scrollGroupIntoView)
    return () => cancelAnimationFrame(frame)
  }, [shouldScrollIntoView, scrollGroupIntoView, isOpen, compact])

  const isSectionActive = items.some(
    (item) => !disabledNavConfig[item.url] && matchPath({ path: item.url, end: item.url === '/' }, pathname)
  )

  // ---- COLLAPSED MODE ----
  if (isVisuallyCollapsed) {
    // Standalone items (Dashboard, Kai) — render item with its page icon
    if (hideLabel) {
      return (
        <SidebarGroup>
          <SidebarMenu>
            {items.map((item) => (
              <NavSectionItem
                key={item.url}
                item={item}
                pathname={pathname}
                showPageIcon
                disabledNavConfig={disabledNavConfig}
                showNewBadges={showNewBadges}
              />
            ))}
          </SidebarMenu>
        </SidebarGroup>
      )
    }

    // Sections — render single section icon
    if (SectionIcon) {
      return (
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton isActive={isSectionActive} tooltip={label} className="relative">
                <SectionIcon className="size-5 shrink-0" />
                {hasNewItem && <NavNewDot className="absolute -right-0.5 -top-0.5" />}
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      )
    }

    return null
  }

  // ---- EXPANDED MODE ----

  // Standalone items (Dashboard, Kai)
  if (hideLabel) {
    return (
      <SidebarGroup>
        <SidebarMenu>
          {items.map((item) => (
            <NavSectionItem
              key={item.url}
              item={item}
              pathname={pathname}
              showPageIcon
              disabledNavConfig={disabledNavConfig}
              showNewBadges={showNewBadges}
              modeSwitch={item.url === DASHBOARD_PATH}
            />
          ))}
        </SidebarMenu>
      </SidebarGroup>
    )
  }

  // Collapsible section
  if (collapsible) {
    // #402: hover davranışı yalnız işaretçi cihazlarda. Dokunmatikte
    // "mouseenter → aç" ardından tap'in "toggle → kapat"ı ile menü hemen
    // kapanırdı; mobilde tek doğru etkileşim dokunarak açmaktır.
    const hoverProps =
      hoverBehavior && !isMobile
        ? {
            onMouseEnter: hoverBehavior.onOpen,
            onMouseLeave: hoverBehavior.onCloseDeferred,
            // Klavye eşdeğeri: gruba focus girince aç, tamamen çıkınca kapat.
            // Capture kullanılır — focus alt bağlantılara iner, bubble
            // aşamasında root'a ulaşmayabilir.
            onFocusCapture: hoverBehavior.onOpen,
            onBlurCapture: (event: React.FocusEvent<HTMLDivElement>) => {
              // Yalnız focus grubun TAMAMEN dışına çıktıysa kapat; link'ler
              // arasında gezinirken kapanmamalı.
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                hoverBehavior.onCloseDeferred()
              }
            },
          }
        : {}

    return (
      <Collapsible
        open={isOpen}
        onOpenChange={() => onToggle?.(label)}
        className="group/collapsible"
        {...hoverProps}
      >
        <SidebarGroup ref={groupRef}>
          <CollapsibleTrigger asChild>
            <SidebarGroupLabel
              lang={groupLabelLang(label)}
              data-active={markActive && isSectionActive ? 'true' : undefined}
              // #402/#1171 erişilebilirlik: SidebarGroupLabel bir <div>; Radix
              // Trigger asChild ile klavye davranışı EKLEMEZ (native <button>
              // bekler) ve aria-expanded'ı rolsüz div'e yazar (axe:
              // aria-allowed-attr). Hover ile açılan grup da, hover kullanamayan
              // klavye kullanıcısına da açılmak ZORUNDA — bu yüzden TÜM açılır
              // bölüm başlıkları elle odaklanabilir düğme yapılır.
              tabIndex={0}
              role="button"
              onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onToggle?.(label)
                }
              }}
              className={cn(
                'cursor-pointer select-none hover:text-sidebar-foreground transition-colors text-xs h-8',
                compact && 'h-[26px]',
                markActive && isSectionActive && 'text-sidebar-foreground font-semibold',
              )}
            >
              {SectionIcon && <SectionIcon className="size-4 shrink-0 mr-1.5" />}
              {label}
              {/* #770: bölüm kapalıyken (accordion closed) içindeki "yeni" item'ı
                  kaçırmasın diye başlıkta nokta görünür; açıkken pill zaten görünür. */}
              {!isOpen && hasNewItem && <NavNewDot className="ml-1.5" />}
              <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-sidebar-foreground/50 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </SidebarGroupLabel>
          </CollapsibleTrigger>
          <CollapsibleContent
            // #402: KRİTİK — açılma animasyonu bitmeden kaydırmak İŞE YARAMAZ.
            // Radix içeriği 0'dan tam yüksekliğe animasyonla açar; ilk kare'de
            // grup hâlâ 36px, container'ın scrollHeight'i küçük, tarayıcı
            // kaydırmayı o anki maksimuma KIRPIYOR. Staging ölçümü: istenen
            // scrollTop 205, gerçekleşen 169.5 (= kapalı haldeki tavan), sonra
            // içerik açılıyor ama kaydırma bir daha denenmiyordu.
            // Bu yüzden animasyon bitince İKİNCİ kez kaydırıyoruz. Yukarıdaki
            // rAF çağrısı da duruyor: hareket-azaltma (reduced motion) açıkken
            // animasyon hiç çalışmaz ve animationend gelmez.
            onAnimationEnd={
              hoverBehavior && isOpen
                ? () => {
                    // ref animasyon bittiği anda okunur (çizim anında değil): compact'ta ilk açılış da kaydırır.
                    if (userOpenedRef.current) scrollGroupIntoView()
                  }
                : undefined
            }
            className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up"
          >
            <SidebarMenu>
              {items.map((item) => (
                <NavSectionItem
                  key={item.url}
                  item={item}
                  pathname={pathname}
                  disabledNavConfig={disabledNavConfig}
                  showNewBadges={showNewBadges}
                />
              ))}
            </SidebarMenu>
          </CollapsibleContent>
        </SidebarGroup>
      </Collapsible>
    )
  }

  // Non-collapsible section (bottom sections)
  return (
    <SidebarGroup>
      <SidebarGroupLabel lang={groupLabelLang(label)} className={cn('text-xs h-8', compact && 'h-[26px]')}>
        {SectionIcon && <SectionIcon className="size-4 shrink-0 mr-1.5" />}
        {label}
      </SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <NavSectionItem
            key={item.url}
            item={item}
            pathname={pathname}
            disabledNavConfig={disabledNavConfig}
            showNewBadges={showNewBadges}
          />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}

function NavSectionItem({
  item,
  pathname,
  showPageIcon = false,
  disabledNavConfig,
  showNewBadges = false,
  modeSwitch = false,
}: {
  item: NavItem
  pathname: string
  showPageIcon?: boolean
  disabledNavConfig: DisabledNavConfig
  /** #770: kullanıcı Ayarlar'da kapattıysa false — hiçbir pill/dot render edilmez. */
  showNewBadges?: boolean
  /** Yalnız Dashboard satırı: hover'da Panelim / Araştırma mod düğmeleri. */
  modeSwitch?: boolean
}) {
  const Icon = showPageIcon ? item.icon : undefined
  const { visualState } = useSidebar()
  const isVisuallyCollapsed = visualState === 'collapsed'
  const showModeSwitch = modeSwitch && !isVisuallyCollapsed
  // #883: salt-okur görünümde `viewAs` yalnız yöneticide geçerli.
  const { profile: modeSwitchProfile } = useUserProfile()
  // Mod düğmesinin üzerindeyken "Dashboard" yazısı solar (düğme adı açılır).
  const [modeHover, setModeHover] = React.useState(false)
  const { openModal: openSupportModal } = useSupportModal()
  // #770: ziyaret rozeti KAPATMAZ — yalnızca newUntil tarihi kontrol edilir.
  const isNew = showNewBadges && isNavItemNew(item.newUntil)

  const disabledFeature = disabledNavConfig[item.url]
  const disableNavigation = Boolean(disabledFeature)
  const [isPreviewPopoverOpen, setIsPreviewPopoverOpen] = React.useState(false)
  const hoverTimeoutRef = React.useRef<number | null>(null)
  const popoverContentId = React.useId()
  const navLinkRef = React.useRef<HTMLElement | null>(null)
  const [tooltipPosition, setTooltipPosition] = React.useState<{
    top: number
    left: number
  } | null>(null)

  const clearHoverTimeout = React.useCallback(() => {
    if (hoverTimeoutRef.current !== null) {
      window.clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }, [])

  const updateTooltipPosition = React.useCallback(() => {
    if (typeof window === 'undefined' || !navLinkRef.current) {
      return
    }

    const rect = navLinkRef.current.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    const clampedTop = Math.min(
      Math.max(midY, 16),
      window.innerHeight - 16,
    )

    setTooltipPosition({
      top: clampedTop,
      left: rect.right + 12,
    })
  }, [])

  const openPopover = React.useCallback(() => {
    clearHoverTimeout()
    setIsPreviewPopoverOpen(true)
    updateTooltipPosition()
  }, [clearHoverTimeout, updateTooltipPosition])

  const scheduleClosePopover = React.useCallback(() => {
    clearHoverTimeout()
    hoverTimeoutRef.current = window.setTimeout(() => {
      setIsPreviewPopoverOpen(false)
    }, 220)
  }, [clearHoverTimeout])

  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current !== null) {
        window.clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  React.useEffect(() => {
    if (!isPreviewPopoverOpen) {
      return
    }

    updateTooltipPosition()

    const handleReposition = () => {
      updateTooltipPosition()
    }

    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)

    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [isPreviewPopoverOpen, updateTooltipPosition])

  const handleDisabledClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()
    openPopover()
  }

  const handleDisabledKeyDown: React.KeyboardEventHandler<HTMLAnchorElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openPopover()
    }
  }

  const handleDisabledMouseEnter: React.MouseEventHandler<HTMLAnchorElement> = () => {
    openPopover()
  }

  const handleDisabledMouseLeave: React.MouseEventHandler<HTMLAnchorElement> = () => {
    scheduleClosePopover()
  }

  const handleDisabledFocus: React.FocusEventHandler<HTMLAnchorElement> = () => {
    openPopover()
  }

  const handleDisabledBlur: React.FocusEventHandler<HTMLAnchorElement> = () => {
    scheduleClosePopover()
  }

  const isActive = disableNavigation
    ? false
    : Boolean(
        matchPath(
          {
            path: item.url,
            end: item.url === '/',
          },
          pathname,
        ),
      )

  const buttonClassName = cn(
    'gap-2 text-sm font-medium transition-colors',
    'group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0',
    'group-data-[collapsible=icon]:bg-transparent',
    isVisuallyCollapsed && 'justify-center gap-2',
    !Icon && !isVisuallyCollapsed && 'pl-7',
    disableNavigation && 'cursor-not-allowed text-sidebar-foreground/30'
  )

  const iconClassName = cn(
    'size-5 shrink-0 transition-colors duration-200',
    disableNavigation
      ? 'text-sidebar-foreground/30'
      : isActive
          ? 'text-sidebar-primary'
          : 'text-sidebar-foreground group-hover/menu-button:text-sidebar-primary',
  )

  const labelClassName = cn(
    'truncate transition-colors duration-200',
    disableNavigation
      ? 'text-sidebar-foreground/40 font-medium'
      : isActive
          ? 'text-sidebar-accent-foreground font-medium'
          : 'text-sidebar-foreground font-medium'
  )

  const disabledNavLinkProps = disableNavigation
    ? {
        onClick: handleDisabledClick,
        onKeyDown: handleDisabledKeyDown,
        onMouseEnter: handleDisabledMouseEnter,
        onMouseLeave: handleDisabledMouseLeave,
        onFocus: handleDisabledFocus,
        onBlur: handleDisabledBlur,
        tabIndex: 0,
        'aria-describedby': popoverContentId,
      }
    : {}

  const baseAriaLabel = disableNavigation
    ? `${item.title} – ${disabledFeature?.heading ?? 'Çok yakında'}`
    : item.description
        ? `${item.title} – ${item.description}`
        : item.title

  // #770: aria-label bu elemanın erişilebilir adını TAMAMEN ELE GEÇİRİR — içindeki
  // sr-only metinler AT tarafından okunmaz. "Yeni özellik" bilgisini buraya
  // ekliyoruz ki hem pill hem collapsed dot için tek noktadan doğru duyurulsun.
  const isLocked = Boolean(item.locked) && !disableNavigation
  const ariaLabelNew = isNew ? `${baseAriaLabel} (yeni özellik)` : baseAriaLabel
  const ariaLabel = isLocked ? `${ariaLabelNew} (Pro'ya özel)` : ariaLabelNew

  const content = (
    <>
      {Icon ? (
        <span className="relative inline-flex shrink-0">
          <Icon className={iconClassName} />
          {isVisuallyCollapsed && isNew && (
            <NavNewDot className="absolute -right-1 -top-1" aria-hidden="true" />
          )}
          {isVisuallyCollapsed && isLocked && (
            <Lock
              className="absolute -bottom-1 -right-1 size-2.5 text-sidebar-foreground/50"
              aria-label="Pro'ya özel"
              role="img"
            />
          )}
        </span>
      ) : null}
      {!isVisuallyCollapsed ? (
        <>
          <span
            className={cn(
              labelClassName,
              showModeSwitch && 'transition-[opacity,color] duration-[180ms] motion-reduce:transition-none',
              showModeSwitch && modeHover && 'opacity-0',
            )}
          >
            {item.title}
          </span>
          {isNew && <NavNewPill className="ml-auto" aria-hidden="true" />}
          {isLocked && (
            <Lock
              className={cn('size-3.5 shrink-0 text-sidebar-foreground/40', !isNew && 'ml-auto')}
              aria-label="Pro'ya özel"
              role="img"
            />
          )}
        </>
      ) : null}
    </>
  )

  const sidebarButton = (
    <SidebarMenuButton
      asChild
      isActive={isActive}
      tooltip={disableNavigation ? undefined : item.title}
      className={buttonClassName}
    >
      {item.externalUrl ? (
        <a
          href={item.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={ariaLabel}
          ref={navLinkRef as React.RefObject<HTMLAnchorElement>}
        >
          {content}
        </a>
      ) : item.action ? (
        <button
          type="button"
          aria-label={ariaLabel}
          ref={navLinkRef as React.RefObject<HTMLButtonElement>}
          onClick={(e) => {
            e.preventDefault()
            item.action?.()
          }}
        >
          {content}
        </button>
      ) : item.modalAction ? (
        <button
          type="button"
          data-support-modal-trigger="true"
          aria-label={ariaLabel}
          ref={navLinkRef as React.RefObject<HTMLButtonElement>}
          onClick={(e) => {
            e.preventDefault()
            openSupportModal()
          }}
        >
          {content}
        </button>
      ) : (
        <NavLink
          to={item.url}
          end={item.url === '/'}
          aria-label={ariaLabel}
          ref={navLinkRef as React.RefObject<HTMLAnchorElement>}
          onPointerEnter={() => preloadPage(item.url)}
          onFocus={() => preloadPage(item.url)}
          {...disabledNavLinkProps}
        >
          {content}
        </NavLink>
      )}
    </SidebarMenuButton>
  )

  const tooltip =
    disableNavigation &&
    tooltipPosition &&
    typeof document !== 'undefined'
      ? createPortal(
          <div
            id={popoverContentId}
            role="tooltip"
            aria-hidden={!isPreviewPopoverOpen}
            className={cn(
              'pointer-events-none fixed z-[999] w-60 -translate-y-1/2 rounded-lg border border-border/60 bg-popover p-4 text-sm text-popover-foreground shadow-xl transition-opacity duration-150 ease-out',
              isPreviewPopoverOpen ? 'pointer-events-auto opacity-100' : 'invisible opacity-0',
            )}
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
            }}
            onMouseEnter={openPopover}
            onMouseLeave={scheduleClosePopover}
          >
            <p className="text-sm font-semibold text-foreground">
              {disabledFeature?.heading ?? 'Çok yakında'}
            </p>
            {disabledFeature?.description ? (
              <p className="mt-1.5 text-sm text-muted-foreground">{disabledFeature.description}</p>
            ) : null}
          </div>,
          document.body,
        )
      : null

  return (
    <>
      <SidebarMenuItem>
        {sidebarButton}
        {showModeSwitch ? <DashboardModeSwitchGate onHoverChange={setModeHover} isAdmin={modeSwitchProfile.isAdmin === true} /> : null}
      </SidebarMenuItem>
      {tooltip}
    </>
  )
}

const DASHBOARD_PATH = '/app/dashboard'
const RESEARCH_MODE_URL = `${DASHBOARD_PATH}?mode=research`

/**
 * Dashboard satırına fareyle gelince (ya da klavyeyle satıra odaklanınca)
 * sağda beliren iki kare düğme: "Panelim" (satıcı paneli) ve "Araştırma".
 * Düğmenin üzerine gelince adı düğmenin içinde açılır, "Dashboard" yazısı
 * solar. Ölçü/renk/geçiş Claude Design "Ana Ekran v2" `modeTabs`'tan.
 */
/** İki düğmenin açık yazı alanı aynı genişlikte: böylece Araştırma'dan Panelim'e
 * geçerken Panelim'in yeri değişmez (Yasin, 2026-09-28). "Araştırma" 12px/600 ≈ 60px. */
const MODE_LABEL_WIDTH = 64

/**
 * Mod düğmeleri yeni ana ekranın parçası: yalnız erişimi olana görünür (D-17).
 * Erişim bilgisi çözülmeden ya da hata/red durumunda render edilmez (bilinmeyen =
 * kapalı, fail-closed). Kanca ayrı bir alt bileşende: NavSectionItem'da koşullu
 * çağrılamaz. Sorgu anahtarı DashboardPage ile ortak; `cacheOnly` ile yalnız
 * önbelleği OKUR: kenar menü her genişleyip açıldığında bu kapı yeniden bağlanır,
 * bu yüzden bağlanınca istek atmamalı (taze okuma sayfanın işi). Soğuk önbellekte
 * (Dashboard hiç açılmadıysa) tek istek atılır.
 */
export function DashboardModeSwitchGate({
  onHoverChange,
  isAdmin = false,
}: {
  onHoverChange: (hovering: boolean) => void
  /** Yönetici mi? Değilse `?viewAs=` yok sayılır. */
  isAdmin?: boolean
}) {
  const location = useLocation()
  // #883: yönetici bir satıcıyı salt-okur görürken (`?viewAs=<uuid>`) düğme hedef satıcının
  // bağlantı durumuna göre çizilir ve geçişler `viewAs`'ı taşır. Yönetici değilse parametre yok sayılır.
  const viewAsParam = new URLSearchParams(location.search).get('viewAs')
  const viewAs = isAdmin && viewAsParam && VIEW_AS_RE.test(viewAsParam) ? viewAsParam : null
  const { isPending, allowed, connected } = useDashboardAccess({ cacheOnly: true, viewAs })
  // Amazon hesabı bağlı olmayana "Panelim" seçimi anlamsız (tek seçenek: Araştırma) → hiç çizilmez
  // (Yasin, 13 Eki 2026). `connected` bilinmiyorsa (eski sunucu yanıtı) eski davranış: göster.
  if (isPending || !allowed || connected === false) return null
  return <DashboardModeSwitch onHoverChange={onHoverChange} viewAs={viewAs} />
}

function DashboardModeSwitch({
  onHoverChange,
  viewAs = null,
}: {
  onHoverChange: (hovering: boolean) => void
  viewAs?: string | null
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const [hovered, setHovered] = React.useState<string | null>(null)
  const researchActive =
    location.pathname === DASHBOARD_PATH && new URLSearchParams(location.search).get('mode') === 'research'

  // #883: salt-okur görünümde iki yöne de `viewAs` korunur (yoksa görünüm yöneticinin kendi ekranına döner).
  const viewAsQuery = viewAs ? `viewAs=${encodeURIComponent(viewAs)}` : ''
  const panelTo = viewAsQuery ? `${DASHBOARD_PATH}?${viewAsQuery}` : DASHBOARD_PATH
  const researchTo = viewAsQuery ? `${RESEARCH_MODE_URL}&${viewAsQuery}` : RESEARCH_MODE_URL

  // Panelim: simge solda, yazı sağa açılır. Araştırma: yazı simgenin solunda.
  const tabs = [
    { key: 'panel', label: 'Panelim', title: 'Satıcı paneli', Icon: LayoutDashboard, active: !researchActive, to: panelTo, iconFirst: true },
    { key: 'research', label: 'Araştırma', title: 'Ürün araştırması', Icon: Search, active: researchActive, to: researchTo, iconFirst: false },
  ]

  const setHover = (key: string | null) => {
    setHovered(key)
    onHoverChange(key !== null)
  }

  return (
    <div
      // Seçim yalnız kutudan çıkınca sıfırlanır: iki düğme arasındaki boşluktan
      // geçerken iki yazı birden kapanıp düğmeler kaymaz.
      onMouseLeave={() => setHover(null)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHover(null)
      }}
      className={cn(
        'absolute right-[6px] top-1/2 z-10 flex -translate-y-1/2 items-center gap-[4px]',
        'pointer-events-none translate-x-[8px] opacity-0',
        'transition-[opacity,transform] duration-[260ms] ease-[cubic-bezier(0.33,1,0.68,1)] motion-reduce:transition-none',
        'group-hover/menu-item:pointer-events-auto group-hover/menu-item:translate-x-0 group-hover/menu-item:opacity-100',
        'group-focus-within/menu-item:pointer-events-auto group-focus-within/menu-item:translate-x-0 group-focus-within/menu-item:opacity-100',
      )}
      data-testid="dashboard-mode-switch"
    >
      {tabs.map(({ key, label, title, Icon, active, to, iconFirst }) => {
        const isHover = hovered === key
        const labelEl = (
          <span
            className={cn(
              'overflow-hidden whitespace-nowrap text-[12px] font-semibold',
              iconFirst ? 'text-left' : 'text-right',
              'transition-[max-width] duration-[220ms] ease-[cubic-bezier(0.33,1,0.68,1)] motion-reduce:transition-none',
            )}
            style={{ maxWidth: isHover ? MODE_LABEL_WIDTH : 0, width: MODE_LABEL_WIDTH, flexShrink: 0 }}
          >
            {label}
          </span>
        )
        const iconEl = <Icon className="size-[14px] shrink-0" aria-hidden="true" />
        return (
          <button
            key={key}
            type="button"
            title={title}
            aria-label={label}
            aria-current={active ? 'page' : undefined}
            data-mode-key={key}
            onMouseEnter={() => setHover(key)}
            onFocus={() => setHover(key)}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              if (!active) navigate(to)
            }}
            className={cn(
              'flex h-[28px] items-center border px-[7px] font-sans outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
              'transition-[gap,background-color,border-color,color,transform] duration-[220ms] ease-[cubic-bezier(0.33,1,0.68,1)] motion-reduce:transition-none',
              'active:translate-y-px',
              isHover ? 'gap-[6px]' : 'gap-0',
            )}
            style={{
              background: isHover ? 'hsl(200 95% 14%)' : 'hsl(200 90% 16%)',
              borderColor: active ? 'rgba(255,168,4,.5)' : isHover ? 'hsl(197 65% 73% / .5)' : 'hsl(200 60% 26%)',
              color: active ? '#FFA804' : isHover ? '#fff' : 'hsl(197 65% 73%)',
            }}
          >
            {iconFirst ? (
              <>
                {iconEl}
                {labelEl}
              </>
            ) : (
              <>
                {labelEl}
                {iconEl}
              </>
            )}
          </button>
        )
      })}
    </div>
  )
}

function SidebarThemeToggle() {
  const { theme, toggleTheme } = useTheme()
  const { visualState } = useSidebar()
  const isDark = theme === 'dark'
  const isCollapsed = visualState === 'collapsed'

  if (isCollapsed) {
    return (
      <div className="flex justify-center px-1 py-1">
        <button
          type="button"
          onClick={toggleTheme}
          className="grid size-9 place-items-center rounded-lg border border-sidebar-border bg-sidebar-accent text-sidebar-foreground/70 transition-colors hover:text-sidebar-accent-foreground"
          aria-label={isDark ? 'Açık temaya geç' : 'Koyu temaya geç'}
        >
          {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </button>
      </div>
    )
  }

  return (
    <div className="px-2 py-1">
      <button
        type="button"
        onClick={toggleTheme}
        className="flex w-full items-center gap-2.5 rounded-lg border border-sidebar-border bg-sidebar-accent px-2.5 py-2 transition-opacity hover:opacity-85"
      >
        <div
          className={cn(
            'relative h-5 w-9 shrink-0 rounded-full transition-all duration-400',
            isDark
              ? 'bg-gradient-to-br from-[hsl(200,80%,18%)] to-[hsl(220,60%,25%)]'
              : 'bg-gradient-to-br from-[hsl(191,70%,38%)] to-[hsl(191,60%,50%)]'
          )}
        >
          <div
            className={cn(
              'absolute top-0.5 grid size-4 place-items-center rounded-full shadow-sm transition-all duration-400',
              isDark ? 'left-[18px] bg-[#ccd]' : 'left-0.5 bg-white'
            )}
          >
            {isDark ? <Moon className="size-2.5" /> : <Sun className="size-2.5" />}
          </div>
        </div>
        <span className="text-xs font-medium text-sidebar-foreground transition-colors">
          {isDark ? 'Koyu Tema' : 'Açık Tema'}
        </span>
      </button>
    </div>
  )
}

function NavUser({
  user,
}: {
  user: {
    name: string
    email: string
  }
}) {
  const { isMobile, visualState } = useSidebar()
  const { isPro, entitlementUnknown } = useSubscriptionContext()
  const navigate = useNavigate()
  const isCollapsed = visualState === 'collapsed'
  const [signOutError, setSignOutError] = React.useState<string | null>(null)
  const fallbackInitials = React.useMemo(() => {
    const nameParts = user.name.split(/\s+/).filter(Boolean)
    if (nameParts.length === 0) {
      const emailInitial = user.email?.charAt(0)?.toUpperCase()
      return emailInitial || 'SN'
    }

    const firstInitial = nameParts[0]?.charAt(0)?.toUpperCase() ?? ''
    const lastInitial =
      nameParts.length > 1
        ? nameParts[nameParts.length - 1]?.charAt(0)?.toUpperCase() ?? ''
        : ''
    const combined = `${firstInitial}${lastInitial}`.trim()
    return combined || firstInitial || 'SN'
  }, [user.email, user.name])

  const handleNavigate = (path: string) => (event: Event) => {
    event.preventDefault()
    event.stopPropagation()
    navigate(path)
  }

  const handleSignOut = async (event: Event) => {
    event.preventDefault()
    event.stopPropagation()
    setSignOutError(null)

    // #863: SSO çerezleri (Grafana/blog) sunucuda silinir; best-effort, çıkışı bloklamaz.
    const ssoCleared = clearSsoCookies()

    if (supabase && isSupabaseConfigured()) {
      const { error } = await supabase.auth.signOut()
      if (error) {
        setSignOutError(error.message)
      }
    }
    await ssoCleared

    navigate('/login')
  }
  return (
    <motion.div
      layout
      transition={{ layout: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } }}
      data-collapsed={isCollapsed}
      className={cn(
        'flex w-full items-center gap-3 motion-safe:transition-[gap,transform] duration-300 ease-out',
        isCollapsed
          ? 'flex-col justify-center gap-2 motion-safe:-translate-y-1'
          : 'flex-row justify-between motion-safe:translate-y-0'
      )}
    >
      <SidebarMenu
        className={cn(
          'transition-[max-width] duration-300 ease-out',
          isCollapsed
            ? 'w-auto items-center justify-center max-w-[2.75rem]'
            : 'flex-1 max-w-[220px]'
        )}
      >
        <SidebarMenuItem className="w-full">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground transition-all duration-300 ease-out"
              >
                <motion.div layoutId="sidebar-avatar" className="flex items-center">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-sidebar-accent text-sidebar-foreground">{fallbackInitials}</AvatarFallback>
                  </Avatar>
                </motion.div>
                <AnimatePresence initial={false} mode="wait">
                  {!isCollapsed ? (
                    <motion.div
                      key="user-meta"
                      layout
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                      className="grid flex-1 min-w-0 text-left text-sm leading-tight"
                    >
                      <span className="flex items-center gap-1.5 truncate font-medium">
                        {user.name}
                        <span className={cn(
                          'inline-flex shrink-0 items-center rounded px-1 py-0.5 text-[10px] font-semibold uppercase leading-none',
                          isPro
                            ? 'bg-brand-chartreuse/15 text-brand-chartreuse'
                            : 'bg-sidebar-foreground/10 text-sidebar-foreground/50'
                        )}>
                          {entitlementUnknown ? '—' : isPro ? 'Pro' : 'Free'}
                        </span>
                      </span>
                      <span className="truncate text-xs text-sidebar-foreground/60">
                        {user.email}
                      </span>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
                <AnimatePresence initial={false}>
                  {!isCollapsed ? (
                    <motion.span
                      key="user-chevron"
                      layout
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                      className="ml-auto flex"
                    >
                      <ChevronsUpDown className="size-4" />
                    </motion.span>
                  ) : null}
                </AnimatePresence>
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg border-sidebar-border bg-sidebar text-sidebar-foreground shadow-xl"
              side={isMobile ? 'bottom' : 'right'}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-sidebar-accent text-sidebar-foreground">{fallbackInitials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium text-sidebar-foreground">{user.name}</span>
                    <span className="truncate text-xs text-sidebar-foreground/50">
                      {user.email}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-sidebar-border" />
              <DropdownMenuItem onSelect={handleNavigate('/app/profile')} className="focus:bg-sidebar-accent focus:text-sidebar-accent-foreground">
                <BadgeCheck className="size-4" />
                Profilim
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleNavigate('/app/billing')} className="focus:bg-sidebar-accent focus:text-sidebar-accent-foreground">
                <CreditCard className="size-4" />
                Billing
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleSignOut} className="focus:bg-sidebar-accent focus:text-sidebar-accent-foreground">
                <LogOut className="size-4" />
                Çıkış yap
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      {signOutError && !isCollapsed ? (
        <p className="px-2 text-xs text-destructive">{signOutError}</p>
      ) : null}
      <motion.div
        layout
        transition={{ layout: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } }}
        className={cn(
          'flex shrink-0 items-center justify-center',
          isCollapsed ? 'w-full' : 'motion-safe:translate-y-0'
        )}
      >
        <SidebarTrigger
          className={cn(
            'h-9 w-9 shrink-0 rounded-lg border border-sidebar-border bg-sidebar-accent text-sidebar-foreground/70 motion-safe:transition-[transform,opacity] duration-300 ease-out hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            isCollapsed ? 'mt-1' : 'self-center'
          )}
          aria-label={isCollapsed ? 'Kenar çubuğunu aç' : 'Kenar çubuğunu daralt'}
          title={isCollapsed ? 'Kenar çubuğunu aç' : 'Kenar çubuğunu daralt'}
        />
      </motion.div>
    </motion.div>
  )
}
